import type { PermissionModule } from "@/D2C/features/auth/utils/permissions";
import type { AppRoute } from "@/shared/types/routerType";

import { ACTIONS } from "./actions";
import {
  MODULE_NAME_ELEMENT_FALLBACK,
  NESTED_ROUTING_ELEMENTS,
  SECURE_ELEMENT_REGISTRY,
} from "./elements";
import { NAV_VISIBLE_ACTION } from "./navigation";
import { SecureModuleElementProps } from "./type";

// Every module-backed page receives these so it decides its own UI (e.g. render a DataTable
// when "list" is granted) from what the backend actually granted — computed once, centrally,
// here — instead of each page independently re-deriving or hardcoding it.

// Builds the private route list straight from the backend module -> path/element mapping.
export function getSecureRoutesFromModules(modules: PermissionModule[]): AppRoute[] {
  return modules.reduce<AppRoute[]>((routes, module) => {
    const Element =
      (module.element_name ? SECURE_ELEMENT_REGISTRY[module.element_name] : undefined) ??
      MODULE_NAME_ELEMENT_FALLBACK[module?.module_name];

    if (!module?.path || !Element) {
      // Backend granted a module the frontend doesn't know how to render yet — skip it instead
      // of crashing, but surface it loudly in dev so the registry gets updated.
      if (import.meta.env.DEV && module?.element_name) {
        console.warn(
          `[permissionBase] No route rendered for module "${module.module_name}": ` +
            `element_name "${module?.element_name}" is not registered in SECURE_ELEMENT_REGISTRY.`
        );
      }
      return routes;
    }

    // A module can grant several actions at once (list, view, add, ...). Resolved once, here,
    // and handed to every module-backed page as `moduleActions` — "view" is what gates the
    // sidebar (NAV_VISIBLE_ACTION), "list" is what a page/DataTable reads to decide whether to
    // render its list, rather than each page re-deriving this from raw permissions.
    const moduleActionNames = module.actions.map((moduleAction) => moduleAction.action_name);
    const primaryAction =
      moduleActionNames.find((name) => name === NAV_VISIBLE_ACTION) ??
      moduleActionNames.find((name) => name === ACTIONS.LIST) ??
      moduleActionNames[0];

    const elementProps: SecureModuleElementProps = {
      moduleName: module.module_name,
      modulePath: module.path,
      moduleActions: moduleActionNames,
      action: primaryAction,
    };

    routes.push({
      path: module.path,
      element: <Element {...elementProps} />,
      module: module.module_name,
      action: primaryAction,
    });

    // Backend module paths drive the route directly — no separate hardcoded frontend path to
    // keep in sync, so a nav item and its route can never point at different URLs.
    if (NESTED_ROUTING_ELEMENTS.has(Element)) {
      routes.push({
        path: `${module.path}/*`,
        element: <Element {...elementProps} />,
        module: module.module_name,
        action: primaryAction,
      });
    }

    return routes;
  }, []);
}
